/**
 * Referral Payout Calculator · 2026-02 (v2)
 *
 * Complete channel-partner commission matrix. Six calculation modes:
 *   1. Advisor Referrals (fixed ₹ amount)
 *   2. Commission on Advisor Work    (% of loan)
 *   3. Commission on DSA Work        (% of loan · AP tier is 25% of DSA commission)
 *   4. Commission on Associate Partner Work (% override on AP earning)
 *   5. Commission on Agency Partner Work    (% override)
 *   6. Commission on Sub Branch Work        (% override)
 *
 * Every payout is subject to the mandatory monthly personal-business threshold
 * (see MIN_BUSINESS). Payouts are released between the 1st and 10th of every
 * following month.
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  Award,
  CalendarClock,
  Calculator,
  CheckCircle2,
  IndianRupee,
  Info,
  Layers,
  Percent,
  Target,
  User,
  UserPlus,
} from "lucide-react";

/* ─── Role dictionary ─────────────────────────────────────────────── */

const ROLE_LABEL = {
  advisor: "Advisor",
  dsa: "DSA",
  ap: "Associate Partner",
  agency: "Agency Partner",
  sub_branch: "Sub Branch",
  branch: "Branch",
};

/* ─── Category rules ──────────────────────────────────────────────── */

const RULES = {
  advisor_referral: {
    label: "Advisor Referrals",
    kind: "fixed",           // fixed ₹ amount per referral
    workerLabel: "Advisor referral onboarded",
    values: {
      dsa: 250,
      ap: 300,
      agency: 350,
      sub_branch: 400,
      branch: 425,
    },
  },
  advisor_work: {
    label: "Commission on Advisor Work",
    kind: "pct_loan",        // % of loan amount
    workerLabel: "Loan amount originated by an Advisor",
    values: {
      dsa: 0.50,
      ap: 0.15,
      agency: 0.25,
      sub_branch: 0.30,
      branch: 0.35,
    },
  },
  dsa_work: {
    label: "Commission on DSA Work",
    kind: "pct_loan",
    workerLabel: "Loan amount originated by a DSA",
    values: {
      ap: 0.125,             // 25% of DSA commission = 25% × 0.50% = 0.125% of loan
      agency: 0.10,
      sub_branch: 0.278,
      branch: 0.50,
    },
    notes: {
      ap:
        "Associate Partner earns 25% of the DSA's own commission on that file (DSA commission = 0.50% of loan, so AP override effectively = 0.125% of loan).",
    },
  },
  ap_work: {
    label: "Commission on Associate Partner Work",
    kind: "pct_ap_earning",  // % of AP's earning on that loan
    workerLabel: "Associate Partner's earning on the file",
    values: {
      agency: 5.00,
      sub_branch: 4.00,
      branch: 3.00,
    },
  },
  agency_work: {
    label: "Commission on Agency Partner Work",
    kind: "pct_agency_earning",
    workerLabel: "Agency Partner's earning on the file",
    values: {
      sub_branch: 3.00,
      branch: 2.50,
    },
  },
  sub_branch_work: {
    label: "Commission on Sub Branch Work",
    kind: "pct_sub_branch_earning",
    workerLabel: "Sub Branch earning on the file",
    values: {
      branch: 1.50,
    },
  },
};

const CATEGORY_OPTIONS = Object.entries(RULES).map(([id, r]) => ({ id, label: r.label }));

/* ─── Mandatory monthly personal business (for eligibility) ────────── */

const MIN_BUSINESS = [
  { role: "DSA",               amount: "₹20,00,000" },
  { role: "Associate Partner", amount: "₹25,00,000" },
  { role: "Agency Partner",    amount: "₹35,00,000" },
  { role: "Sub Branch",        amount: "₹45,00,000" },
  { role: "Branch",            amount: "₹70,00,000" },
];

/* ─── Formatting helpers ──────────────────────────────────────────── */

const inr = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

function computePayout(category, recipient, baseAmountRupees) {
  const rule = RULES[category];
  if (!rule) return null;
  const val = rule.values[recipient];
  if (val === undefined) return null;

  if (rule.kind === "fixed") return { amount: val, isFixed: true };

  const base = Number(baseAmountRupees) || 0;
  if (base <= 0) return { amount: 0, isFixed: false, pct: val };

  const payout = (base * val) / 100;
  return { amount: payout, isFixed: false, pct: val };
}

/* ─── Component ───────────────────────────────────────────────────── */

export default function ReferralCalculator() {
  const [form, setForm] = useState({
    category: "advisor_referral",
    recipient: "dsa",
    base_amount: "",
    referrer_name: "",
    referred_name: "",
  });
  const [locked, setLocked] = useState(null);

  const activeRule = RULES[form.category];
  const recipientOptions = useMemo(
    () => Object.keys(activeRule.values).map((k) => ({ value: k, label: ROLE_LABEL[k] })),
    [activeRule]
  );

  /* If the recipient becomes invalid after switching category, snap to first. */
  const safeRecipient = activeRule.values[form.recipient] !== undefined
    ? form.recipient
    : recipientOptions[0].value;

  const livePayout = useMemo(
    () => computePayout(form.category, safeRecipient, form.base_amount),
    [form.category, safeRecipient, form.base_amount]
  );

  const isFixed = activeRule.kind === "fixed";
  const noteForRecipient = activeRule.notes?.[safeRecipient];

  const calculate = (e) => {
    e.preventDefault();
    setLocked({
      category: form.category,
      categoryLabel: activeRule.label,
      recipient: safeRecipient,
      recipientLabel: ROLE_LABEL[safeRecipient],
      base: Number(form.base_amount) || 0,
      payout: livePayout,
      referrer_name: form.referrer_name.trim() || "—",
      referred_name: form.referred_name.trim() || "—",
      workerLabel: activeRule.workerLabel,
      note: noteForRecipient,
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#04102B] via-[#08194a] to-[#020a1f] text-white">
      <header className="border-b border-white/10 bg-black/20 backdrop-blur">
        <div className="max-w-6xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
          <Link
            to="/"
            data-testid="rc-back-home"
            className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-[#F0D788]"
          >
            <ArrowLeft size={14} /> Sahyog Finance &amp; Noble Infinity
          </Link>
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#D4AF37]">
            Referral Payout Calculator · v2
          </div>
        </div>
      </header>

      <main id="main-content" tabIndex={-1} className="max-w-6xl mx-auto px-5 md:px-8 py-10 md:py-14 space-y-10">
        {/* Hero */}
        <section>
          <div className="text-[11px] tracking-[0.3em] uppercase text-[#D4AF37]">
            Instant estimator · Updated Feb 2026
          </div>
          <h1 className="mt-2 font-serif text-4xl md:text-5xl leading-tight">
            Referral &amp; Override Payout Calculator
          </h1>
          <p className="mt-3 text-white/70 leading-relaxed max-w-3xl">
            Six commission categories covering every channel-partner scenario — from a one-off
            advisor referral bounty right up to a Branch head&apos;s cascading override on Sub Branch
            business. Pick the category, choose the recipient, enter the base amount &amp; get the
            payout in real time.
          </p>
        </section>

        {/* Calculator */}
        <form
          onSubmit={calculate}
          data-testid="referral-calculator-form"
          className="rounded-3xl bg-white/[0.04] border border-[#D4AF37]/25 p-6 md:p-10 space-y-6"
        >
          {/* Category / Recipient row */}
          <div className="grid md:grid-cols-2 gap-4">
            <Field
              label="Commission category"
              icon={<Layers size={13} className="text-[#F0D788]" />}
            >
              <select
                data-testid="rc-category"
                value={form.category}
                onChange={(e) =>
                  setForm((f) => ({ ...f, category: e.target.value }))
                }
                className={inputCls}
              >
                {CATEGORY_OPTIONS.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field
              label="Who is the payout for?"
              icon={<UserPlus size={13} className="text-[#F0D788]" />}
            >
              <select
                data-testid="rc-recipient"
                value={safeRecipient}
                onChange={(e) =>
                  setForm((f) => ({ ...f, recipient: e.target.value }))
                }
                className={inputCls}
              >
                {recipientOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          {/* Base amount (hidden when fixed) */}
          {!isFixed && (
            <Field
              label={`Base amount (${activeRule.workerLabel}) in ₹`}
              icon={<IndianRupee size={13} className="text-[#F0D788]" />}
            >
              <input
                data-testid="rc-base-amount"
                type="number"
                min="0"
                step="1000"
                value={form.base_amount}
                onChange={(e) => setForm((f) => ({ ...f, base_amount: e.target.value }))}
                placeholder="e.g. 500000"
                className={inputCls}
              />
            </Field>
          )}

          {/* Names */}
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Recipient name (optional)" icon={<User size={13} className="text-[#F0D788]" />}>
              <input
                data-testid="rc-referrer-name"
                value={form.referrer_name}
                onChange={(e) => setForm((f) => ({ ...f, referrer_name: e.target.value }))}
                placeholder="e.g. Ravi Patel"
                className={inputCls}
              />
            </Field>
            <Field label="Business originated by (optional)" icon={<User size={13} className="text-[#F0D788]" />}>
              <input
                data-testid="rc-referred-name"
                value={form.referred_name}
                onChange={(e) => setForm((f) => ({ ...f, referred_name: e.target.value }))}
                placeholder="e.g. Priya Shah"
                className={inputCls}
              />
            </Field>
          </div>

          {noteForRecipient && (
            <div className="rounded-xl border border-sky-500/40 bg-sky-500/[0.08] px-4 py-3 text-xs text-sky-100 flex items-start gap-2">
              <Info size={14} className="mt-0.5 shrink-0" />
              <span>{noteForRecipient}</span>
            </div>
          )}

          {/* Live pill */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 rounded-2xl border border-[#D4AF37]/40 bg-[#D4AF37]/[0.08] p-5">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-[#F0D788]">
                Live estimate
              </div>
              <div
                data-testid="rc-live-amount"
                className="font-serif text-3xl md:text-4xl mt-1 text-white"
              >
                {livePayout === null
                  ? "Not eligible"
                  : `₹${inr(livePayout.amount)}`}
              </div>
              <div className="text-[11px] text-white/60 mt-0.5 flex items-center gap-1.5">
                <Percent size={11} />
                {isFixed
                  ? "Fixed per-referral bounty"
                  : livePayout?.pct !== undefined
                    ? `${livePayout.pct}% of the base amount`
                    : "Enter a base amount to see the payout"}
              </div>
            </div>
            <button
              type="submit"
              data-testid="rc-calculate-btn"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full btn-gold text-sm font-semibold whitespace-nowrap"
            >
              <Calculator size={14} /> Calculate Payout
            </button>
          </div>
        </form>

        {/* Result card */}
        {locked && (
          <section
            data-testid="rc-result-card"
            className="rounded-3xl border border-[#D4AF37]/40 bg-gradient-to-br from-[#0e2a6b] via-[#1c3d92] to-[#0A1F44] p-7 md:p-10"
          >
            <div className="text-[11px] uppercase tracking-[0.3em] text-[#F0D788]">
              Payout summary
            </div>
            <div className="mt-3 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
              <div>
                <h2 className="font-serif text-2xl md:text-3xl text-white leading-tight">
                  {locked.categoryLabel} → {locked.recipientLabel}
                </h2>
                <div className="text-white/60 text-xs md:text-sm mt-1">
                  Payout as per the Sahyog channel-partner agreement · v2 (Feb 2026).
                </div>
              </div>
              <div
                data-testid="rc-result-amount"
                className="font-serif text-4xl md:text-5xl text-[#F0D788]"
              >
                {locked.payout === null ? "—" : `₹${inr(locked.payout.amount)}`}
              </div>
            </div>

            <div className="mt-6 grid md:grid-cols-2 gap-3 text-sm">
              <SummaryLine label="Recipient" value={`${locked.referrer_name} (${locked.recipientLabel})`} />
              <SummaryLine label="Business originated by" value={locked.referred_name} />
              {!locked.payout?.isFixed && (
                <>
                  <SummaryLine label="Base amount" value={`₹${inr(locked.base)}`} />
                  <SummaryLine label="Applied rate" value={`${locked.payout?.pct ?? "—"}%`} />
                </>
              )}
            </div>

            {locked.note && (
              <div className="mt-5 rounded-xl border border-sky-500/40 bg-sky-500/[0.08] px-4 py-3 text-xs text-sky-100 flex items-start gap-2">
                <Info size={14} className="mt-0.5 shrink-0" />
                <span>{locked.note}</span>
              </div>
            )}

            <div className="mt-5 rounded-xl bg-emerald-500/[0.08] border border-emerald-500/30 px-4 py-3 text-sm text-emerald-100 flex items-start gap-2">
              <CheckCircle2 size={15} className="mt-0.5 shrink-0" />
              <span>
                {locked.referrer_name} will receive{" "}
                <strong>₹{inr(locked.payout?.amount ?? 0)}</strong> once the qualifying business
                is verified. Payouts are released between the <strong>1st and 10th of the
                following month</strong>, subject to the mandatory monthly personal-business
                threshold shown below.
              </span>
            </div>
          </section>
        )}

        {/* All rules matrix */}
        <section data-testid="rc-rules-section">
          <div className="text-[11px] tracking-[0.3em] uppercase text-[#D4AF37]">
            Reference · Complete commission matrix
          </div>
          <h2 className="mt-1 font-serif text-2xl md:text-3xl">All 20+ payout rules</h2>
          <p className="text-white/60 text-sm mt-1 max-w-3xl">
            Six categories covering every referral / override in the channel. Fixed ₹ amounts are
            per-file bounties; percentages compute against the corresponding base amount (loan or
            higher-level earning).
          </p>

          <div className="mt-5 grid lg:grid-cols-2 gap-5">
            {Object.entries(RULES).map(([id, rule]) => (
              <div
                key={id}
                data-testid={`rc-rule-block-${id}`}
                className="rounded-2xl border border-white/10 bg-white/[0.03] p-5"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.24em] text-[#D4AF37]">
                      Category
                    </div>
                    <h3 className="mt-1 font-serif text-lg text-white">{rule.label}</h3>
                  </div>
                  <span
                    className={`text-[10px] uppercase tracking-[0.18em] px-2.5 py-1 rounded-full border ${
                      rule.kind === "fixed"
                        ? "text-[#F0D788] border-[#D4AF37]/40 bg-[#D4AF37]/10"
                        : "text-emerald-200 border-emerald-500/40 bg-emerald-500/10"
                    }`}
                  >
                    {rule.kind === "fixed" ? "Fixed ₹" : "%"}
                  </span>
                </div>
                <div className="text-[11px] text-white/50 mt-1">
                  Base: <span className="text-white/70">{rule.workerLabel}</span>
                </div>
                <ul className="mt-4 divide-y divide-white/10">
                  {Object.entries(rule.values).map(([roleKey, val]) => (
                    <li
                      key={roleKey}
                      className="flex items-center justify-between py-2.5 text-sm"
                    >
                      <span className="text-white/80 flex items-center gap-2">
                        <Award size={13} className="text-[#F0D788]" />
                        {ROLE_LABEL[roleKey]}
                      </span>
                      <span className="font-semibold text-[#F0D788] tabular-nums">
                        {rule.kind === "fixed" ? `₹${inr(val)}` : `${val}%`}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* Mandatory monthly business */}
        <section data-testid="rc-min-business" className="rounded-3xl border border-rose-500/30 bg-rose-500/[0.05] p-6 md:p-8">
          <div className="flex items-center gap-2 text-rose-200 text-[11px] tracking-[0.24em] uppercase">
            <Target size={14} /> Mandatory monthly personal business
          </div>
          <h2 className="mt-2 font-serif text-2xl md:text-3xl text-white">
            Eligibility threshold to unlock any payout
          </h2>
          <p className="mt-2 text-white/70 text-sm max-w-3xl">
            To be entitled to referral commissions &amp; overrides, every partner must clock the
            following personal business volume each calendar month. Miss the threshold and the
            month&apos;s payout is deferred to the next qualifying month.
          </p>
          <div className="mt-5 grid grid-cols-1 min-[400px]:grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {MIN_BUSINESS.map((m) => (
              <div
                key={m.role}
                data-testid={`rc-minbiz-${m.role.toLowerCase().replace(/\s+/g, "-")}`}
                className="rounded-xl border border-rose-500/30 bg-black/25 p-4 text-center"
              >
                <div className="text-[10px] uppercase tracking-[0.22em] text-rose-200">
                  {m.role}
                </div>
                <div className="mt-2 font-serif text-xl md:text-2xl text-white tabular-nums">
                  {m.amount}
                </div>
                <div className="mt-1 text-[10px] text-white/45 uppercase tracking-wider">
                  per month
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Payout window */}
        <section
          data-testid="rc-payout-window"
          className="rounded-3xl border border-[#D4AF37]/30 bg-white/[0.03] p-6 md:p-8"
        >
          <div className="flex items-center gap-2 text-[#F0D788] text-[11px] tracking-[0.24em] uppercase">
            <CalendarClock size={14} /> Payout window
          </div>
          <h2 className="mt-2 font-serif text-2xl md:text-3xl text-white">
            Released between the 1st and 10th of every month
          </h2>
          <p className="mt-2 text-white/70 text-sm max-w-3xl">
            All qualifying commissions computed for a given calendar month are consolidated,
            reconciled with disbursement reports, and credited to your registered bank account
            between the <strong className="text-white">1st and 10th of the following month</strong>.
            Any bank / KYC discrepancies will pause the credit until the file is cleared by the
            Sahyog Finance channel team.
          </p>
        </section>

        {/* Disclaimer */}
        <section
          data-testid="rc-disclaimer"
          className="rounded-2xl border border-white/10 bg-black/25 px-5 py-4 text-xs text-white/60 leading-relaxed flex items-start gap-3"
        >
          <Info size={16} className="text-[#D4AF37] mt-0.5 shrink-0" />
          <span>
            <strong className="text-white/80">Note:</strong> This calculator is indicative only.
            Actual commission is paid after (a) verification &amp; approval by the Sahyog Finance
            channel-partner team, (b) achievement of the mandatory monthly personal-business
            threshold, and (c) statutory deductions (TDS, GST as applicable). All amounts are
            subject to the current commission schedule which may change with prior written notice.
          </span>
        </section>

        {/* Small footer CTA */}
        <section className="text-center pt-2">
          <Link
            to="/apply/associate"
            data-testid="rc-cta-associate"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full btn-gold text-sm font-semibold"
          >
            Become an Associate Partner <IndianRupee size={13} />
          </Link>
        </section>
      </main>
    </div>
  );
}

/* ─── helpers ─────────────────────────────────────────────────────── */
const inputCls =
  "w-full px-4 py-2.5 rounded-lg bg-white/[0.05] border border-[#D4AF37]/20 outline-none focus:border-[#D4AF37]/60 text-sm text-white placeholder:text-white/30";

function Field({ label, icon, children }) {
  return (
    <label className="block">
      <span className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-white/55 mb-1.5">
        {icon} {label}
      </span>
      {children}
    </label>
  );
}

function SummaryLine({ label, value }) {
  return (
    <div className="rounded-xl border border-white/10 bg-black/20 p-3.5">
      <div className="text-[10px] uppercase tracking-[0.2em] text-white/45">{label}</div>
      <div className="mt-1 text-white/85 break-words">{value}</div>
    </div>
  );
}
